import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Search } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const { getCartCount } = useCart();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    // Close search dropdown on outside click
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
      setShowSearch(false);
    }
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const performSearch = async (query) => {
    setSearching(true);
    try {
      const res = await axios.get(`${API}/products/search`, { params: { q: query, limit: 8 } });
      setSearchResults(res.data);
      setShowSearch(true);
    } catch (err) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearch(false);
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-purple-700 via-blue-600 to-purple-600 text-white py-1.5 sm:py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-xs sm:text-sm">
            <div className="flex items-center gap-2 sm:gap-6">
              <span className="hidden xs:inline">📞 (206) 712-0952</span>
              <span className="hidden md:inline">📧 info@uskesupplyllc.com</span>
              {/* USKE Procurement Portal is a standalone product — accessible only via its direct URL. */}
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {user ? (
                <>
                  <Link 
                    to={isAdmin() ? '/admin' : '/account'} 
                    className="hover:text-slate-200 transition-colors truncate max-w-[100px] sm:max-w-none"
                  >
                    <span className="hidden sm:inline">Welcome, </span>{user.name?.split(' ')[0]}
                  </Link>
                  <button onClick={handleLogout} className="hover:text-slate-200 transition-colors">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="hover:text-slate-200 transition-colors">
                    Login
                  </Link>
                  <Link to="/register" className="hover:text-slate-200 transition-colors">
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="container mx-auto px-4 py-2 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img 
              src="/logo.jpg" 
              alt="USKE SUPPLY LLC" 
              className="h-8 w-8 sm:h-12 sm:w-12 object-contain rounded-lg shadow-md"
            />
            <div className="flex flex-col">
              <span className="font-bold text-base sm:text-xl text-slate-800">USKE SUPPLY</span>
              <span className="text-xs text-slate-600">LLC</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <Link to="/" className="text-slate-700 hover:text-purple-600 font-medium transition-colors">
              Home
            </Link>
            <Link to="/products" className="text-slate-700 hover:text-purple-600 font-medium transition-colors">
              All Products
            </Link>
            {user && (
              <Link to="/dashboard" className="text-slate-700 hover:text-purple-600 font-medium transition-colors" data-testid="dashboard-nav-link">
                Dashboard
              </Link>
            )}
            <Link to="/track-order" className="text-slate-700 hover:text-purple-600 font-medium transition-colors">
              Track Order
            </Link>
            <Link to="/about" className="text-slate-700 hover:text-purple-600 font-medium transition-colors">
              About Us
            </Link>
            <Link to="/contact" className="text-slate-700 hover:text-purple-600 font-medium transition-colors">
              Contact
            </Link>
          </div>

          {/* Search Bar - Desktop */}
          <div className="hidden md:block relative" ref={searchRef}>
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 lg:w-56 pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                data-testid="search-input"
              />
            </form>
            {/* Search Dropdown */}
            {showSearch && searchResults.length > 0 && (
              <div className="absolute top-full mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto" data-testid="search-dropdown">
                {searchResults.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.id}`}
                    className="flex items-center gap-3 p-3 hover:bg-purple-50 transition-colors border-b border-slate-100 last:border-0"
                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                    data-testid={`search-result-${product.id}`}
                  >
                    <img src={product.image} alt={product.name} className="w-10 h-10 object-cover rounded" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{product.name}</p>
                      <p className="text-xs text-purple-600 font-semibold">${product.price}</p>
                    </div>
                  </Link>
                ))}
                <button
                  onClick={handleSearchSubmit}
                  className="w-full p-2 text-center text-sm text-purple-600 hover:bg-purple-50 font-medium"
                >
                  View all results for "{searchQuery}"
                </button>
              </div>
            )}
            {showSearch && searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
              <div className="absolute top-full mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-50 p-4 text-center" data-testid="search-no-results">
                <p className="text-sm text-slate-500">No products found for "{searchQuery}"</p>
              </div>
            )}
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/cart" className="relative">
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10">
                <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 text-slate-700" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center text-[10px] sm:text-xs">
                    {getCartCount()}
                  </span>
                )}
              </Button>
            </Link>
            <Link to={user ? (isAdmin() ? '/admin' : '/account') : '/login'}>
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10">
                <User className="h-4 w-4 sm:h-5 sm:w-5 text-slate-700" />
              </Button>
            </Link>
            <button
              className="lg:hidden p-1"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t pt-4">
            {/* Mobile Search */}
            <form onSubmit={handleSearchSubmit} className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-purple-500"
                data-testid="mobile-search-input"
              />
            </form>
            <div className="flex flex-col gap-2">
              <Link to="/" className="text-slate-700 hover:text-purple-600 py-2 text-sm" onClick={() => setIsMenuOpen(false)}>
                Home
              </Link>
              <Link to="/products" className="text-slate-700 hover:text-purple-600 py-2 text-sm" onClick={() => setIsMenuOpen(false)}>
                All Products
              </Link>
              {user && (
                <Link to="/dashboard" className="text-slate-700 hover:text-purple-600 py-2 text-sm" onClick={() => setIsMenuOpen(false)}>
                  Dashboard
                </Link>
              )}
              <Link to="/track-order" className="text-slate-700 hover:text-purple-600 py-2 text-sm" onClick={() => setIsMenuOpen(false)}>
                Track Order
              </Link>
              <Link to="/about" className="text-slate-700 hover:text-purple-600 py-2 text-sm" onClick={() => setIsMenuOpen(false)}>
                About Us
              </Link>
              <Link to="/contact" className="text-slate-700 hover:text-purple-600 py-2 text-sm" onClick={() => setIsMenuOpen(false)}>
                Contact
              </Link>
              {isAdmin() && (
                <Link to="/admin" className="text-purple-600 hover:text-purple-700 py-2 text-sm font-medium" onClick={() => setIsMenuOpen(false)}>
                  Admin Panel
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Categories Bar - Scrollable on mobile */}
      <div className="bg-slate-50 border-t">
        <div className="container mx-auto px-4 py-2 sm:py-3">
          <div className="flex gap-2 sm:gap-3 justify-start sm:justify-center overflow-x-auto pb-1 scrollbar-hide">
            {categories.map(cat => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="text-xs sm:text-sm text-slate-600 hover:text-purple-600 hover:bg-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-md transition-all whitespace-nowrap flex-shrink-0"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
